// Групирането по категория за справка „По категории".
//
// Ендпойнтът `ordered-items` не знае за категории като цяло — той връща по
// един ред на (продаваем, станция, вид). Кофите се сглобяват тук, в браузъра.
//
// Продуктът и същият продаваем, продаден като ДОБАВКА, влизат в една и съща
// кофа: за категорията е важно колко е донесла, а не как е маркирана. (В „По
// артикули" двата реда нарочно остават разделени — цените им се различават.)

import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import type { OrderedItem } from "../../../../api/reports";
import { deltaPercent as percentDelta } from "../reportDelta";

export type CategoryRow = {
  /** `null` = артикул без категория. Такъв ред НЕ се изхвърля. */
  categoryId: number | null;
  /** Продажбите от обедно меню в категорията — отделен ред с етикет „Обедно". */
  isLunch: boolean;
  categoryName: string;
  quantity: number;
  revenue: number;
  /** `null`, когато количеството е нула — средна цена от нищо не съществува. */
  avgPrice: number | null;
  /** Дял от оборота за периода, в проценти. */
  sharePercent: number;
  /** Δ на оборота спрямо предходния период; `null` = не бива да се рисува. */
  deltaPercent: number | null;
};

export type CategoriesTotals = {
  categories: number;
  quantity: number;
  revenue: number;
};

type Bucket = {
  categoryId: number | null;
  isLunch: boolean;
  categoryName: string;
  quantity: number;
  revenue: number;
};

/** Ключът на кофата: id-то, а за артикулите без категория — една обща кофа.
 *  Продаденото от обедно меню е ОТДЕЛНА кофа на същата категория. */
const bucketKeyOf = (item: OrderedItem): string =>
  `${item.category_id === null ? "none" : String(item.category_id)}${item.is_lunch ? ":lunch" : ""}`;

const collect = (items: OrderedItem[]): Map<string, Bucket> => {
  const buckets = new Map<string, Bucket>();
  for (const item of items) {
    const key = bucketKeyOf(item);
    const existing = buckets.get(key);
    const quantity = parseDecimal(item.count);
    const revenue = parseDecimal(item.total_sum);
    if (existing) {
      existing.quantity += quantity;
      existing.revenue += revenue;
      continue;
    }
    buckets.set(key, {
      categoryId: item.category_id,
      isLunch: item.is_lunch === true,
      // Името идва вече преведено от BE-то (`language_code`); липсва ли —
      // кофата се казва с тире, за да не изглежда като категория „null".
      categoryName: item.category_name ?? EM_DASH,
      quantity,
      revenue,
    });
  }
  return buckets;
};

/**
 * Редовете на таблицата. `previousItems` е `null`, когато сравнението е
 * изключено — тогава нито един ред няма Δ.
 */
export const buildCategoryRows = (
  items: OrderedItem[],
  previousItems: OrderedItem[] | null,
): CategoryRow[] => {
  const buckets = collect(items);
  const previousBuckets = previousItems ? collect(previousItems) : null;

  const totalRevenue = Array.from(buckets.values()).reduce(
    (sum, bucket) => sum + bucket.revenue,
    0,
  );

  return Array.from(buckets.entries()).map(([key, bucket]) => {
    const previous = previousBuckets?.get(key);
    return {
      categoryId: bucket.categoryId,
      isLunch: bucket.isLunch,
      categoryName: bucket.categoryName,
      quantity: bucket.quantity,
      revenue: bucket.revenue,
      avgPrice: bucket.quantity > 0 ? bucket.revenue / bucket.quantity : null,
      sharePercent:
        totalRevenue > 0 ? (bucket.revenue / totalRevenue) * 100 : 0,
      // Категория, която я е нямало миналия период, влиза с нула — а ръст от
      // нищо не е „+100 %", а „ново": `percentDelta` връща `null` и чип не се
      // рисува (виж `reportDelta`).
      deltaPercent:
        previousBuckets === null
          ? null
          : percentDelta(bucket.revenue, previous?.revenue ?? 0),
    };
  });
};

export const totalsOf = (rows: CategoryRow[]): CategoriesTotals => ({
  // Обедният ред на категорията не е нова категория
  categories: new Set(rows.map((row) => row.categoryId ?? "none")).size,
  quantity: rows.reduce((sum, row) => sum + row.quantity, 0),
  revenue: rows.reduce((sum, row) => sum + row.revenue, 0),
});
